
import { db } from '../firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  addDoc, 
  onSnapshot,
  query,
  orderBy,
  limit, 
  Unsubscribe
} from 'firebase/firestore';
import { UserProfile, MeetingEntry, ReviewLog, ServiceEntry, InventoryItem } from '../types';

// Default "Empty State" for new users
const NEW_USER_TEMPLATE: Partial<UserProfile> = {
  sobrietyDates: [{ id: '1', label: 'Sobriety', definition: 'Freedom from active addiction.', date: new Date().toISOString().split('T')[0] }],
  circleItems: [],
  customQuestions: [
    { id: '1', text: 'Were you resentful today? Why?', isDefault: true },
    { id: '2', text: 'Were you selfish today? Why?', isDefault: true },
    { id: '3', text: 'Were you dishonest today? How so?', isDefault: true },
    { id: '4', text: 'Were you afraid today? Of what or whom?', isDefault: true },
    { id: '5', text: 'Do you owe an apology? To whom, and when will you give it?', isDefault: true },
  ],
  sharingConfig: {
      shareInventory: false,
      shareReviews: false,
      shareService: true,
      shareCircles: true,
      shareSobrietyDates: true
  },
  proteges: [],
  stepProgress: Array.from({ length: 12 }, (_, i) => ({
      stepNumber: i + 1,
      status: i === 0 ? 'active' : 'locked',
      homeworkCompleted: false,
      feedbackHistory: []
  }))
};

/**
 * Ensures the user document exists. If not, creates it with default data.
 */
export const initializeUser = async (user: any) => {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    const newProfile = {
      id: user.uid,
      email: user.email,
      firstName: user.displayName?.split(' ')[0] || 'Friend',
      lastInitial: user.displayName?.split(' ')[1]?.[0] || '',
      emailVerified: user.emailVerified,
      phone: '',
      location: '',
      ...NEW_USER_TEMPLATE
    };
    await setDoc(userRef, newProfile);
  }
};

/**
 * Real-time listener for the Core Profile (Settings, Circles, etc)
 */
export const subscribeToProfile = (uid: string, callback: (data: Partial<UserProfile>) => void): Unsubscribe => {
    return onSnapshot(doc(db, 'users', uid), (doc) => {
        if (doc.exists()) {
            callback(doc.data() as Partial<UserProfile>);
        }
    });
};

/**
 * Real-time listener for Sub-Collections (Meetings, Reviews, Service, Inventory)
 */
export const subscribeToSubCollection = (
    uid: string, 
    collectionName: 'meetings' | 'reviews' | 'service' | 'inventory', 
    callback: (data: any[]) => void
): Unsubscribe => {
    // Inventory doesn't always have a date field, so we might sort differently or let client sort
    let q;
    if (collectionName === 'inventory') {
         q = query(collection(db, 'users', uid, collectionName), limit(200));
    } else {
         q = query(collection(db, 'users', uid, collectionName), orderBy('date', 'desc'), limit(100));
    }
    
    return onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(items);
    });
};

// --- WRITE OPERATIONS ---

export const updateUserField = async (uid: string, field: string, value: any) => {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { [field]: value });
};

export const addMeetingLog = async (uid: string, meeting: Omit<MeetingEntry, 'id'>) => {
    await addDoc(collection(db, 'users', uid, 'meetings'), meeting);
};

export const addServiceLog = async (uid: string, service: Omit<ServiceEntry, 'id'>) => {
    await addDoc(collection(db, 'users', uid, 'service'), service);
};

export const addReviewLog = async (uid: string, review: Omit<ReviewLog, 'id'>) => {
    await addDoc(collection(db, 'users', uid, 'reviews'), review);
};

export const addInventoryItem = async (uid: string, item: Omit<InventoryItem, 'id'>) => {
    await addDoc(collection(db, 'users', uid, 'inventory'), item);
};

export const deleteSubCollectionItem = async (uid: string, collectionName: string, itemId: string) => {
    await deleteDoc(doc(db, 'users', uid, collectionName, itemId));
};
